import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { FloorPlan, LocationNode } from '@oscom/shared';
import { floorPlanPreviewUrl, listFloorPlans } from '../api/floor-plans';
import { FloorPlanUploadDialog } from './FloorPlanUploadDialog';

/** Card "Pläne" on a location: thumbnails of its plans and the upload entry. */
export function LocationPlansSection({
  customerId,
  location,
  locations,
  canWrite,
}: {
  customerId: string;
  location: LocationNode;
  locations: readonly LocationNode[];
  canWrite: boolean;
}): ReactNode {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<FloorPlan[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(
    (cancelled: { current: boolean }) => {
      setPlans(undefined);
      listFloorPlans(customerId, location.id)
        .then((response) => {
          if (cancelled.current) return;
          setPlans(response.plans);
          setLoadError(undefined);
        })
        .catch((caught: unknown) => {
          if (cancelled.current) return;
          setLoadError(
            caught instanceof Error ? caught.message : 'Pläne konnten nicht geladen werden.',
          );
        });
    },
    [customerId, location.id],
  );

  useEffect(() => {
    const cancelled = { current: false };
    load(cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [load]);

  const base = `/kunden/${encodeURIComponent(customerId)}/plaene`;

  return (
    <div className="card">
      <h2>Pläne</h2>
      {loadError ? (
        <p className="notice notice--warning">{loadError}</p>
      ) : plans === undefined ? (
        <p className="muted" role="status" aria-live="polite">
          Pläne werden geladen ...
        </p>
      ) : plans.length === 0 ? (
        <p className="muted">An diesem Ort ist noch kein Plan hochgeladen.</p>
      ) : (
        <div className="plan-gallery">
          {plans.map((plan) => (
            <Link key={plan.id} to={`${base}/${encodeURIComponent(plan.id)}`} title={plan.name}>
              <img
                loading="lazy"
                className="plan-thumb"
                alt={plan.name}
                src={floorPlanPreviewUrl(customerId, plan)}
              />
            </Link>
          ))}
        </div>
      )}
      {canWrite && !uploading ? (
        <p>
          <button
            type="button"
            className="button button--primary"
            onClick={() => setUploading(true)}
          >
            Plan hochladen
          </button>
        </p>
      ) : null}
      {canWrite && uploading ? (
        <FloorPlanUploadDialog
          customerId={customerId}
          locations={locations}
          presetLocationId={location.id}
          onCancel={() => setUploading(false)}
          onDone={(plan) => navigate(`${base}/${encodeURIComponent(plan.id)}`)}
        />
      ) : null}
    </div>
  );
}
